import React, { useMemo, useCallback, useState } from "react";
import SideMenu, { SideMenuTitle } from "@amzn/meridian/side-menu";
import { useLocation, useNavigate, Location } from "react-router-dom";
import { SideMenuLink } from "@amzn/meridian/side-menu";
import Theme from "@amzn/meridian/theme";
import { getBrowserPath } from "@/app/routes/appBasename";
import { RootState } from "@/store";
import { useSelector } from "react-redux";
import { getMenuLinks, createClickHandler, MenuLink } from "./utils/menuLinks";
import { AuthInfo } from "@/app/types";
import { PermissionsState } from "@/features/yard360/store/permissions/types";
import { resolveSelectedSite } from "@/utils/selectedSiteStorage";
import { Heading } from "@amzn/meridian";
import "./styles.scss";

const getCurrentBrowserPath = (route: string): string =>
  getBrowserPath({ route, pathname: window.location.pathname });

const getNavigationPath = (path: string): string => {
  if (!path.startsWith("/")) return path;

  const url = new URL(path, window.location.origin);
  if (url.searchParams.has("site")) return path;

  const selectedSite = resolveSelectedSite(new URLSearchParams(window.location.search).get("site"));
  if (!selectedSite) return path;

  url.searchParams.set("site", selectedSite);
  return `${url.pathname}${url.search}${url.hash}`;
};

export const selectFeatureFlags = (state: RootState): Record<string, { enabled: boolean }> =>
  state.featureFlags as Record<string, { enabled: boolean }>;
export const selectAuthInfo = (state: RootState) => state.authInfo;
export const selectPermissions = (state: RootState) => state.permissions;

export interface SideMenuCoreProps {
  open: boolean;
  location: Location;
  navigate: (path: string) => void;
  featureFlags: Record<string, { enabled: boolean }>;
  authInfo: AuthInfo;
  permissions?: PermissionsState;
  getMenuLinksFunc?: typeof getMenuLinks;
  createClickHandlerFunc?: typeof createClickHandler;
  themeMode?: "dark" | "light";
}

/**
 * Core SideMenu component that accepts dependencies as props for better testability.
 *
 * This component contains all the business logic and rendering logic but accepts
 * its dependencies (location, navigate, store data) as props rather than using
 * hooks directly. This makes it much easier to test since we can pass in mock
 * values without having to mock React Router or Redux hooks.
 *
 * @param location - Current route location from React Router
 * @param navigate - Navigation function from React Router
 * @param featureFlags - Feature flags from Redux store
 * @param authInfo - Authentication info from Redux store
 * @param getMenuLinksFunc - Function to generate menu links (injectable for testing)
 * @param createClickHandlerFunc - Function to create click handlers (injectable for testing)
 * @param themeMode - Theme mode for the component
 */
export const SideMenuCore: React.FC<SideMenuCoreProps> = ({
  open,
  location,
  navigate,
  featureFlags,
  authInfo,
  permissions,
  getMenuLinksFunc = getMenuLinks,
  createClickHandlerFunc = createClickHandler,
  themeMode = "dark",
}) => {
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});

  // Memoize menu links to prevent unnecessary recalculations
  const menuLinks = useMemo(() => {
    return getMenuLinksFunc({
      featureFlags,
      authInfo,
      permissions,
    });
  }, [featureFlags, authInfo, permissions, getMenuLinksFunc]);

  // Memoize navigation handler
  const handleNavigationClick = useMemo(() => {
    return createClickHandlerFunc(navigate);
  }, [navigate, createClickHandlerFunc]);

  // Memoize the render function for menu links
  const renderMenuLink = useCallback(
    (menuLink: MenuLink) => {
      const { path, label, isSelected, children, target, rel } = menuLink;
      const hasChildren = children && children.length > 0;
      const isCurrentSelected = isSelected(location.pathname);
      const navigationPath = getNavigationPath(path);
      const href = getCurrentBrowserPath(navigationPath);

      const handleClick = (clickedPath: string) => {
        if (target === "_blank") return undefined;
        return (event: React.MouseEvent) => {
          if (hasChildren) {
            // For parent menus with children, just toggle the open state
            setOpenMenus((prev) => ({
              ...prev,
              [path]: !prev[path],
            }));
          } else {
            // For regular links, navigate to the path
            handleNavigationClick(clickedPath)(event);
          }
        };
      };

      const handleEscape = (parentPath: string) => () => {
        navigate(parentPath);
      };

      return (
        <SideMenuLink
          key={path}
          href={href}
          data-testid={`side-nav-${label?.replace(/\s/g, "")}`}
          selected={isCurrentSelected}
          open={hasChildren ? openMenus[path] : undefined}
          onClick={handleClick(navigationPath)}
          target={target}
          rel={rel}
          aria-label={`Navigate to ${label}`}
        >
          {label}
          {hasChildren &&
            children.map((child) => (
              <SideMenuLink
                key={child.path}
                href={getCurrentBrowserPath(getNavigationPath(child.path))}
                data-testid={`side-nav-${child.label?.replace(/\s/g, "")}`}
                selected={child.isSelected(location.pathname)}
                onClick={
                  child.target === "_blank"
                    ? undefined
                    : handleNavigationClick(getNavigationPath(child.path))
                }
                onEscape={handleEscape(path)}
                target={child.target}
                rel={child.rel}
                aria-label={`Navigate to ${child.label}`}
              >
                {child.label}
              </SideMenuLink>
            ))}
        </SideMenuLink>
      );
    },
    [location.pathname, handleNavigationClick, navigate, openMenus]
  );

  return (
    <Theme mode={themeMode} data-testid="side-menu-wrapper">
      <SideMenu data-testid="side-menu-container" aria-label="Main navigation" open={open}>
        <SideMenuTitle>
          <Heading level={3} className="text-color-inverted">
            Relay Yard Hub
          </Heading>
        </SideMenuTitle>
        {menuLinks.map(renderMenuLink)}
      </SideMenu>
    </Theme>
  );
};

interface SideMenuWrapperProps {
  open: boolean;
}

const SideMenuWrapper: React.FC<SideMenuWrapperProps> = ({ open }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const featureFlags = useSelector(selectFeatureFlags);
  const authInfo = useSelector(selectAuthInfo);
  const permissions = useSelector(selectPermissions);

  return (
    <SideMenuCore
      open={open}
      location={location}
      navigate={navigate}
      featureFlags={featureFlags}
      authInfo={authInfo}
      permissions={permissions}
    />
  );
};

SideMenuWrapper.displayName = "SideMenuWrapper";

export default SideMenuWrapper;
